import { type JSX, useEffect, useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";
import isEmpty from "lodash/isEmpty";

import categoryService from "app/common/services/categoryService";
import type { ICategory } from "app/common/services/categoryService.types";
import { swSettings } from "app/common/services/swSettings";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { useSelectorTrigger } from "app/components/filters-bar/useSelectorTrigger";
import { MMXAlertWithPlainTooltip } from "app/components/MMXAlertWithPlainTooltip";
import { FooterComponent } from "app/components/widget/WidgetFooter";
import { i18nFilter } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { Loader } from "../common/Loader";
import { PngDownload } from "../common/PngDownload";

import { AllTrafficChannelsOverviewBarChartBenchmark } from "./AllTrafficChannelsOverviewBarChartBenchmark";
import { AllTrafficChannelsOverviewBarChartCompareMode } from "./AllTrafficChannelsOverviewBarChartCompareMode";
import { AllTrafficChannelsOverviewBarChartSingleMode } from "./AllTrafficChannelsOverviewBarChartSingleMode";
import { hasAnyRealTraffic, transformDomainTrafficData } from "./AllTrafficChannelsOverviewBarChartUtils";
import { NoDataWrapperComp } from "./ChannelsOverviewBarChartConfig";
import { numbersDataFormat, percentDataFormat } from "./ChannelsOverviewBarChartContainerFormat";
import { ChannelsOverviewBarChartSwitcher, useDataFormat } from "./ChannelsOverviewBarChartSwitcher";
import ViewByDomainChart from "./components/view-by-domain-chart/ViewByDomainChart";
import ViewBySwitcher, { EViewBySwitcherItems } from "./components/view-by-switcher/ViewBySwitcher";
import { useTrafficChannelsData } from "./hooks/useTrafficChannelsData";
import {
    BoxSubtitleWrapper,
    LoaderWrapper,
    MMXAlertWrapper,
    RightSection,
    StyledBoxContainer,
    StyledHeaderTitle,
    TitleBox,
    ViewByDomainChrtContainer,
    WidgetFooterContainer,
} from "./StyledComponents";
import type { TrafficDataByDeviceType, IndustryDataByDeviceType } from "./types";

const dataModeMap = {
    [percentDataFormat]: "percent",
    [numbersDataFormat]: "number",
};

const { Channels, Domain } = EViewBySwitcherItems;

function select(mmxData: TrafficDataByDeviceType | null, industryData: IndustryDataByDeviceType | undefined) {
    return { trafficData: mmxData, industryData };
}

interface IAllTrafficChannelsOverviewBarChartContainerProps {
    isCategoryBenchmarkVisible?: boolean;
    getCtaLink?: (params) => { href: string; target: string };
    unlockHook?: any;
    isWWO?: boolean;
    hideFooter?: boolean;
}

export const AllTrafficChannelsOverviewBarChartContainer = ({
    isCategoryBenchmarkVisible = false,
    getCtaLink,
    unlockHook = null,
    isWWO = false,
    hideFooter = false,
}: IAllTrafficChannelsOverviewBarChartContainerProps) => {
    const { params, state } = useCurrentStateAndParams();
    const { country, webSource, duration, comparedDuration, category, isWWW, key } = params;
    const i18n = i18nFilter();
    const { selectorHandlers } = useSelectorTrigger("channels-overview-bar-chart");
    const durationObject = useMemo(() => {
        const configId = proNavigator.getConfigId(state);
        return DurationService.getDurationData(duration, comparedDuration, configId);
    }, [duration, comparedDuration]);
    const fromDate = durationObject.raw.from;
    const toDate = durationObject.raw.to;
    const is28d = duration === "28d";
    const isCompare = key.split(",").length > 1;
    const includeSubDomains = isWWW === "*";
    const timeGranularity = is28d ? "Daily" : "Monthly";
    const [selectedCategory, setSelectedCategory] = useState<ICategory | undefined>(
        categoryService.getCategory(category),
    );
    const [viewBy, setViewBy] = useState<EViewBySwitcherItems>(() => {
        const { channelsOverviewViewByType } = proNavigator.getParams();
        if (!isCompare) {
            return Channels;
        }
        return channelsOverviewViewByType === EViewBySwitcherItems[Domain].toString() ? Domain : Channels;
    });
    const chartRef = useRef<HTMLElement>(null);
    const component = swSettings.current;
    const newMMXAlgoStartData = component.resources.NewAlgoMMX;
    const isShowMMXAlertBell = () =>
        (webSource === devicesTypes.MOBILE || webSource === devicesTypes.TOTAL) &&
        fromDate.isBefore(dayjs(newMMXAlgoStartData)) &&
        toDate.isAfter(dayjs(newMMXAlgoStartData));

    const [dataFormat, setDataFormat] = useDataFormat(isCompare);

    const viewBySwitcherClick = (selectedViewBy: EViewBySwitcherItems) => {
        setViewBy(selectedViewBy);
    };

    const ctaLink = getCtaLink
        ? getCtaLink({
              ...params,
              webSource: webSource,
              category: category,
          })
        : null;

    const trackLink = () => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", "Marketing Mix/Channels Overview/see full overview");
    };

    const { data, isLoading, isError } = useTrafficChannelsData({ category: selectedCategory, select });

    const subtitleFilters = useMemo(
        () => [
            {
                filter: "date",
                value: {
                    from: fromDate.valueOf(),
                    to: toDate.valueOf(),
                    useRangeDisplay: duration !== "28d" && fromDate.format("YYYY-MM") !== toDate.format("YYYY-MM"),
                },
                onClick: selectorHandlers.onDateClick,
                onMouseEnter: selectorHandlers.onDateHover,
            },
            {
                filter: "country",
                countryCode: country,
                value: CountryService.getCountryById(country)?.text,
                onClick: selectorHandlers.onCountryClick,
                onMouseEnter: selectorHandlers.onCountryHover,
            },
            {
                filter: "webSource",
                value: webSource,
                onMouseEnter: selectorHandlers.onWebSourceHover,
            },
        ],
        [fromDate, toDate, country, webSource, duration, selectorHandlers],
    );

    useEffect(() => {
        const $chart = $(chartRef.current);
        $chart.on("click", "[data-ts-category]", (evt) => {
            const trafficSource = evt.target.getAttribute("data-ts-category");
            allTrackers.trackEvent("Internal Link", "click", `Traffic Sources Overview/${trafficSource}`);
        });

        return () => {
            $chart.off("click", "[data-ts-category]");
        };
    }, []);

    const getAllTrafficVisioButtons = () => {
        const onAllTrafficVisualizationClick = (selectedDataFormat) => {
            setDataFormat(selectedDataFormat);

            allTrackers.dispatchTrackEvent(
                "Graph Filter",
                "click",
                `                Bar Chart/Traffic Sources Overview/${dataModeMap[selectedDataFormat] || ""}`,
            );
        };

        return (
            <>
                {isCompare ? (
                    <ViewBySwitcher
                        onViewChange={viewBySwitcherClick}
                        selectedView={viewBy}
                    />
                ) : null}
                <ChannelsOverviewBarChartSwitcher
                    onClick={(selectedDataFormat) => onAllTrafficVisualizationClick(selectedDataFormat)}
                    dataFormat={dataFormat}
                />
            </>
        );
    };
    const hasAnyRealTrafficValue = useMemo(() => {
        return hasAnyRealTraffic(data?.trafficData?.[devicesTypes.TOTAL]);
    }, [data]);
    const getComponent = () => {
        if (!data || isEmpty(data) || isError || !hasAnyRealTrafficValue) {
            return <NoDataWrapperComp size="large" />;
        }
        let contentComponent: JSX.Element;
        if (selectedCategory && selectedCategory.id !== "no-category" && viewBy === EViewBySwitcherItems.Channels) {
            contentComponent = (
                <AllTrafficChannelsOverviewBarChartBenchmark
                    category={selectedCategory}
                    data={data}
                    dataFormat={dataFormat}
                    fromDate={fromDate}
                    onCategorySelected={setSelectedCategory}
                />
            );
        } else if (!isCompare) {
            contentComponent = (
                <AllTrafficChannelsOverviewBarChartSingleMode
                    isCategoryBenchmarkVisible={isCategoryBenchmarkVisible}
                    data={data.trafficData}
                    isWWO={isWWO}
                    dataFormat={dataFormat}
                    fromDate={fromDate}
                    onCategorySelected={setSelectedCategory}
                />
            );
        } else if (viewBy === EViewBySwitcherItems.Domain) {
            const totalDeviceData = data.industryData
                ? { ...data.trafficData[devicesTypes.TOTAL], category: data.industryData.Total }
                : data.trafficData[devicesTypes.TOTAL];

            contentComponent = (
                <ViewByDomainChrtContainer>
                    <ViewByDomainChart
                        data={transformDomainTrafficData(totalDeviceData)}
                        isPercentageDataMode={dataModeMap[dataFormat] === dataModeMap[percentDataFormat]}
                    />
                </ViewByDomainChrtContainer>
            );
        } else {
            contentComponent = (
                <AllTrafficChannelsOverviewBarChartCompareMode
                    isCategoryBenchmarkVisible={isCategoryBenchmarkVisible}
                    data={data.trafficData}
                    dataFormat={dataFormat}
                    fromDate={fromDate}
                    onCategorySelected={setSelectedCategory}
                />
            );
        }

        return (
            <>
                {contentComponent}
                {ctaLink && !hideFooter ? (
                    <WidgetFooterContainer>
                        <FooterComponent
                            href={ctaLink.href}
                            onClick={trackLink}
                            text="wwo.traffic.sources.button"
                            unlockHook={unlockHook}
                            target={ctaLink.target}
                        />
                    </WidgetFooterContainer>
                ) : null}
            </>
        );
    };
    return (
        <StyledBoxContainer
            ref={chartRef}
            data-automation={"channelsOverviewContainer"}
        >
            <TitleBox>
                <FlexColumn>
                    <StyledHeaderTitle>
                        <BoxTitle
                            tooltip={i18n("mmx.trafficSources.bar.tooltip")}
                            customElement={
                                isShowMMXAlertBell() ? (
                                    <MMXAlertWrapper>
                                        <MMXAlertWithPlainTooltip text="mmx.total.new.algo.subtitle.filters.tooltip.content" />
                                    </MMXAlertWrapper>
                                ) : null
                            }
                        >
                            {i18n("mmx.trafficSources.bar.title")}
                        </BoxTitle>
                    </StyledHeaderTitle>
                    <BoxSubtitleWrapper>
                        <StyledBoxSubtitle>
                            <BoxSubtitle filters={subtitleFilters} />
                        </StyledBoxSubtitle>
                    </BoxSubtitleWrapper>
                </FlexColumn>
                {!isWWO ? (
                    <RightSection className={"hidden-print"}>
                        {getAllTrafficVisioButtons()}
                        <PngDownload
                            selectedMetricName={i18n("mmx.trafficSources.bar.title")}
                            chartRef={chartRef}
                        />
                        <AddToDashboard
                            webSource={webSource}
                            type="BarChartMMXDashboard"
                            metric="TrafficSourcesOverview"
                            modelType="fromWebsite"
                            filters={{
                                includeSubDomains,
                                timeGranularity,
                            }}
                            overrideAddToDashboardParams={{
                                dataMode: dataFormat,
                            }}
                            buttonProps={
                                viewBy === EViewBySwitcherItems.Domain
                                    ? {
                                          isDisabled: true,
                                          tooltipContent:
                                              "mmx.channelsoverview.addtodashboard.bydomain.disabled.tooltip",
                                      }
                                    : undefined
                            }
                        />
                    </RightSection>
                ) : null}
            </TitleBox>
            {isLoading ? (
                <LoaderWrapper>
                    <Loader />
                </LoaderWrapper>
            ) : (
                getComponent()
            )}
        </StyledBoxContainer>
    );
};
